import { Controller, useFormContext } from 'react-hook-form';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormHelperText from '@mui/material/FormHelperText';
import { JsonField } from 'src/components/json-field';
import { Iconify } from 'src/components/iconify';
import { useCallback } from 'react';

export function RHFMarketsConfigSelect({ name, label, options, slotProps, helperText, ...other }) {
  const { control } = useFormContext();
  const getMarketIndex = (arr, key) => arr.findIndex((obj) => obj.key === key);

  const toggleMarket = (arr, key) => {
    const index = getMarketIndex(arr, key);
    if (index !== -1) {
      const copy = [...arr];
      copy.splice(index, 1);
      return copy;
    }
    return [...arr, { key, value: '' }];
  };

  const onJSONChange = useCallback((field, index, key, value) => {
    const newValue = [...field.value];
    newValue[index] = { key, value };
    try {
      JSON.parse(value);
      newValue[index].valid = true;
    } catch {
      newValue[index].valid = false;
    }
    field.onChange(newValue);
  }, []);

  const labelFor = (key) => options.find((o) => o.value === key)?.label || key;

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]}
      render={({ field, fieldState: { error } }) => {
        const selectedKeys = field.value.map((item) => item.key);

        return (
          <FormControl component="fieldset" sx={{ width: '100%', ...slotProps?.wrap }}>
            {label && (
              <FormLabel
                component="legend"
                {...slotProps?.formLabel}
                sx={{ mb: 1, typography: 'body2', ...slotProps?.formLabel?.sx }}
              >
                {label}
              </FormLabel>
            )}

            {/* Select which markets are enabled */}
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  sm: 'repeat(4, 1fr)',
                  md: 'repeat(10, 1fr)',
                },
              }}
              {...other}
            >
              {options.map((option) => {
                const checked = selectedKeys.includes(option.value);
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    color={checked ? 'primary' : 'default'}
                    variant={checked ? 'filled' : 'outlined'}
                    onClick={() => field.onChange(toggleMarket(field.value, option.value))}
                    icon={
                      checked ? (
                        <Iconify icon="eva:checkmark-fill" width={16} />
                      ) : (
                        <Iconify icon="mingcute:add-line" width={16} />
                      )
                    }
                    sx={{ width: '100%', justifyContent: 'flex-start' }}
                    {...slotProps?.chip}
                  />
                );
              })}
            </Box>

            {/* Configure each selected market */}
            {field.value.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Configure selected
                </Typography>
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(3, 1fr)',
                      md: 'repeat(4, 1fr)',
                    },
                  }}
                >
                  {field.value.map((item, index) => {
                    const errMsg = Array.isArray(error) ? error[index]?.value?.message : '';
                    return (
                      <Box
                        key={item.key}
                        sx={{
                          p: 1.5,
                          minWidth: 0,
                          overflow: 'hidden',
                          borderRadius: 1.5,
                          border: (theme) =>
                            `solid 1px ${
                              errMsg ? theme.palette.error.main : theme.palette.divider
                            }`,
                          bgcolor: (theme) => theme.palette.background.neutral,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="subtitle2">{labelFor(item.key)}</Typography>
                          <Tooltip title="Remove market">
                            <IconButton
                              size="small"
                              sx={{ m: -0.5 }}
                              onClick={() => field.onChange(toggleMarket(field.value, item.key))}
                            >
                              <Iconify icon="mingcute:close-line" width={16} />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        <JsonField
                          value={item.value ?? ''}
                          onChange={(value) => onJSONChange(field, index, item.key, value)}
                          maxHeight={`${5 * 19}px`}
                          hideActions
                          hideLineNumbers
                          square
                          compactFormat
                          wrap
                        />
                        {errMsg && (
                          <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
                            {errMsg}
                          </FormHelperText>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}

            {(!!error || helperText) && (
              <FormHelperText error={!!error} sx={{ mx: 0, mt: 1 }} {...slotProps?.formHelperText}>
                {error && !Array.isArray(error) ? error?.message : helperText}
              </FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
}
